// ==============================================
// Register Page
// ==============================================

import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RegisterFormData, PlayerLevel } from '../types';
import { metadataAPI } from '../services/api';
import logo from '../assets/images/logo.svg';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register, error, clearError } = useAuth();

  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    password_confirmation: '',
    first_name: '',
    last_name: '',
    phone: '',
    level_id: null,
    external_profile_url: '',
  });
  const [levels, setLevels] = useState<PlayerLevel[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    metadataAPI.getAll().then(res => {
      if (res.data?.player_levels) {
        setLevels(res.data.player_levels.sort((a, b) => a.rank_order - b.rank_order));
      }
    }).catch(() => {});
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'level_id' ? (value ? Number(value) : null) : value,
    }));
    clearError();
    setValidationError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.level_id) {
      setValidationError('Te rugăm să selectezi nivelul de joc');
      return false;
    }

    if (formData.password !== formData.password_confirmation) {
      setValidationError('Parolele nu se potrivesc');
      return false;
    }

    if (formData.password.length < 8) {
      setValidationError('Parola trebuie să aibă cel puţin 8 caractere');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await register(formData);
      navigate('/dashboard');
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = validationError || error;

  return (
    <div className="page">
      <div className="auth-wrapper">
        <div className="auth-card fade-up" style={{ maxWidth: 560 }}>
          <div className="auth-logo">
            <img src={logo} alt="TT Platform" style={{ height: '48px' }} />
          </div>
          <h1 className="auth-title">Creează-ţi contul</h1>
          <p className="auth-sub">Alătură-te comunităţii române de tenis de masă</p>

          {displayError && <div className="alert alert-danger mb-3">{displayError}</div>}

          <form onSubmit={handleSubmit}>
            <div className="row g-3 mb-3">
              <div className="col-6">
                <label htmlFor="first_name" className="form-label">Prenume</label>
                <input type="text" id="first_name" name="first_name" className="form-control" placeholder="Ion" value={formData.first_name} onChange={handleChange} required />
              </div>
              <div className="col-6">
                <label htmlFor="last_name" className="form-label">Nume</label>
                <input type="text" id="last_name" name="last_name" className="form-control" placeholder="Popescu" value={formData.last_name} onChange={handleChange} required />
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">Adresă de email</label>
              <input type="email" id="email" name="email" className="form-control" placeholder="tu@exemplu.com" value={formData.email} onChange={handleChange} required autoComplete="email" />
            </div>
            <div className="mb-3">
              <label htmlFor="phone" className="form-label">Număr de telefon</label>
              <input type="tel" id="phone" name="phone" className="form-control" placeholder="+40712345678" value={formData.phone} onChange={handleChange} required />
            </div>
            <div className="mb-3">
              <label htmlFor="level_id" className="form-label">Nivel de joc</label>
              <select id="level_id" name="level_id" className="form-select" value={formData.level_id ?? ''} onChange={handleChange} required>
                <option value="" disabled>Selectează nivelul tău</option>
                {levels.map(level => (
                  <option key={level.id} value={level.id}>{level.display_name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label htmlFor="external_profile_url" className="form-label">
                URL Profil Extern <span className="text-muted" style={{ fontWeight: 400 }}>(opțional)</span>
              </label>
              <input
                type="url"
                id="external_profile_url"
                name="external_profile_url"
                className="form-control"
                placeholder="https://..."
                value={formData.external_profile_url}
                onChange={handleChange}
              />
              <small className="text-muted">Ex: Amatur, ETTU, ITTF etc.</small>
            </div>
            <div className="mb-3">
              <label htmlFor="password" className="form-label">Parolă</label>
              <input type="password" id="password" name="password" className="form-control" placeholder="Cel puţin 8 caractere" value={formData.password} onChange={handleChange} required autoComplete="new-password" />
            </div>
            <div className="mb-4">
              <label htmlFor="password_confirmation" className="form-label">Confirmă parola</label>
              <input type="password" id="password_confirmation" name="password_confirmation" className="form-control" placeholder="Re-introdu parola" value={formData.password_confirmation} onChange={handleChange} required autoComplete="new-password" />
            </div>
            <button type="submit" className="btn btn-primary w-100 py-2 mb-3" disabled={isSubmitting}>
              {isSubmitting
                ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Se creează contul…</>
                : 'Creează cont'
              }
            </button>
          </form>

          <div className="text-center" style={{ fontSize: '.875rem', color: 'var(--tt-text-muted)' }}>
            Ai deja un cont?{' '}
            <Link to="/login" style={{ color: 'var(--tt-primary)', textDecoration: 'none', fontWeight: 500 }}>
              Autentifică-te
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
